import {
    MAX_SELECTION_DURATION,
    MIN_SELECTION_DURATION,
    TIME_PRECISION,
    VERTICAL_THUMBNAIL_HEIGHT_RATIO,
} from './constants';
import { type ThumbnailMetadata } from './types';

interface GetEffectiveThumbnailsPerSecondArgs {
    duration: number;
    thumbnailsPerSecond: number;
    maxThumbnails: number;
    thumbnailCountOverride?: number | null;
}

/**
 * Compute the effective thumbnails-per-second for a given video.
 *  1. Duration-based adjustment — short videos get higher density so the
 *     filmstrip isn't mostly empty (≤10s → 1.0 TPS, ≤20s → 0.75 TPS).
 *  2. Adaptive max cap — ensures we never exceed `maxThumbnails` total,
 *     scaling TPS down proportionally for longer videos.
 */
export function getEffectiveThumbnailsPerSecond({
    duration,
    thumbnailsPerSecond,
    maxThumbnails,
    thumbnailCountOverride = null,
}: GetEffectiveThumbnailsPerSecondArgs): number {
    if (thumbnailCountOverride !== null) {
        return thumbnailCountOverride / duration;
    }

    let adjustedThumbnailsPerSecond = thumbnailsPerSecond;
    if (duration <= 10) {
        adjustedThumbnailsPerSecond = 1.0;
    } else if (duration <= 20) {
        adjustedThumbnailsPerSecond = 0.75;
    }

    const adaptiveMaxThumbnails = Math.min(maxThumbnails, Math.max(10, Math.ceil(duration)));
    const maxThumbnailsPerSecond = adaptiveMaxThumbnails / duration;

    return Math.min(adjustedThumbnailsPerSecond, maxThumbnailsPerSecond);
}

/**
 * Independent of container width: a resize changes how wide each frame is
 * drawn, never which frame it is. Lets frame extraction skip resize churn.
 */
export function calculateThumbnailTimes(duration: number, thumbnailsPerSecond: number): number[] {
    if (thumbnailsPerSecond <= 0) return [];

    const thumbnailInterval = 1 / thumbnailsPerSecond;
    const count = Math.ceil(duration / thumbnailInterval);
    if (count <= 0) return [];

    return Array.from({ length: count }, (_, i) => i * thumbnailInterval);
}

interface CalculateThumbnailsArgs {
    duration: number;
    stripHeight: number;
    thumbnailsPerSecond: number;
    containerWidth: number;
    isVertical?: boolean;
}

export function calculateThumbnails({
    duration,
    stripHeight,
    thumbnailsPerSecond,
    containerWidth,
    isVertical,
}: CalculateThumbnailsArgs): ThumbnailMetadata[] {
    if (containerWidth <= 0) return [];

    const times = calculateThumbnailTimes(duration, thumbnailsPerSecond);
    if (times.length === 0) return [];

    const thumbnailWidth = containerWidth / times.length;

    return times.map((timeInSeconds, i) => ({
        position: i * thumbnailWidth,
        width: thumbnailWidth,
        height: isVertical ? VERTICAL_THUMBNAIL_HEIGHT_RATIO * thumbnailWidth : stripHeight,
        timeInSeconds,
    }));
}

export function pixelsToTime(pixels: number, duration: number, stripWidth: number): number {
    return (pixels / stripWidth) * duration;
}

export function timeToAbsolutePixel(time: number, videoDuration: number, stripWidth: number): number {
    if (videoDuration <= 0) return 0;
    return (time / videoDuration) * stripWidth;
}

export function constrainSelectionTime(time: number, duration: number, videoDuration: number): number {
    return Math.max(0, Math.min(videoDuration - duration, time));
}

/** Constrain left trim: clamp to [0, endTime-min] and [endTime-max, endTime-min]. */
export function constrainTrimLeft(
    newStartTime: number,
    endTime: number,
    minDuration: number = MIN_SELECTION_DURATION,
    maxDuration: number = MAX_SELECTION_DURATION,
): number {
    let constrained = Math.max(0, newStartTime);
    constrained = Math.min(constrained, endTime - minDuration);
    constrained = Math.max(constrained, endTime - maxDuration);
    return constrained;
}

/** Constrain right trim: clamp to [startTime+min, videoDuration] and [startTime+min, startTime+max]. */
export function constrainTrimRight(
    newEndTime: number,
    startTime: number,
    videoDuration: number,
    minDuration: number = MIN_SELECTION_DURATION,
    maxDuration: number = MAX_SELECTION_DURATION,
): number {
    let constrained = Math.min(videoDuration, newEndTime);
    constrained = Math.max(constrained, startTime + minDuration);
    constrained = Math.min(constrained, startTime + maxDuration);
    return constrained;
}

export function roundToPrecision(value: number, precision: number = TIME_PRECISION): number {
    const multiplier = Math.pow(10, precision);
    return Math.round(value * multiplier) / multiplier;
}

/** Format a time offset as MM:SS for the player's elapsed/total readout. */
export function formatTimeDisplay(seconds: number): string {
    const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}
